import React, { FC } from 'react';
import { ObjectType } from '~/types/ObjectType';
import { TextField } from '~/components';
import { Skeleton } from '@material-ui/lab';
import { usePermissionsOptimized, useTranslate } from 'react-admin';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { buildProfileLink, findParent, getName } from '~/utils/identities';
import BusinessIcon from '@material-ui/icons/Store';
import FeedbackIcon from '@material-ui/icons/StarHalfRounded';
import AccountBalanceWallet from '@material-ui/icons/AccountBalanceWallet';
import { ArchiveIcon as PnLIcon, ReaderIcon as TransactionIcon } from '~/img';
import {
  PersonCircleIcon as IndividualIcon,
  PersonCircleIcon as EmployeeIcon,
  TrailSign,
} from '~/img';
import StarIcon from '@material-ui/icons/Star';
import { useReferenceEntity } from '~/hooks';
import cx from 'classnames';
import { Entity } from '~/types/Entity';

interface Ref {
  id: string;
  type: ObjectType;
}

interface Props {
  label?: string;
  record: Ref;
  parent?: Ref;
  withDefaultIcon?: boolean;
  icon?: React.ReactNode;
  className?: string;
  withLabel?: boolean;
  withLink?: boolean;
  renderer?: (entity: Entity | undefined) => any;
}

const ReferenceField: FC<Props> = (props) => {
  const {
    record,
    parent,
    label,
    withDefaultIcon = false,
    icon = undefined,
    className,
    withLabel = true,
    withLink = true,
    renderer,
  } = props;
  const { id, type } = record;

  const translate = useTranslate();

  const classes = useStyles();

  // usePermissionsOptimized, not usePermissions: it seeds state from a cache shared
  // across mounts, so remounted rows in virtualized lists already have permissions
  // on their first render and the link doesn't flicker while scrolling.
  const { permissions } = usePermissionsOptimized();

  const { data: entity, isLoading, partnerId } = useReferenceEntity(record);

  // if there is no parent in props, we try to find it in entity response
  const { id: parentId, type: parentType } = parent ?? findParent(type, entity) ?? {};

  if (isLoading) return <Skeleton width='100%' height='18px' />;

  // The dashboard-identity profile page (/team/:id/show) requires `identity.view`.
  // Without it /objects/collect returns name-only and that page is inaccessible,
  // so we don't render a link the viewer can't follow.
  // TODO: other object types have the same problem — their profile pages are also
  // permission-gated. Replace this special case with a `type -> required permission`
  // map and check it for every type.
  const canLink =
    withLink && (type !== 'DASHBOARD_IDENTITY' || permissions?.includes('identity.view'));

  const t = (key: string): string => translate(makeLabel(key));
  const makeTextProps = () => ({
    label: withLabel ? label ?? t(type) : undefined,
    allowEmpty: true,
    showLink:
      canLink && entity
        ? buildProfileLink(type, entity?.actualId || id, parentType, parentId, partnerId)
        : undefined,
    children: renderer ? (
      renderer(entity)
    ) : (
      <div className={classes.ellipsis}>{getName?.[type]?.(entity ?? {}) ?? id}</div>
    ),
  });

  return (
    <TextField
      {...makeTextProps()}
      className={cx(classes.ellipsis, className)}
      icon={icon ? icon : withDefaultIcon ? makeIcon[type]?.() || <></> : undefined}
    />
  );
};

const makeLabel = (key: string): string => `resources.events.show.${key}`;

export const makeIcon: Record<ObjectType, () => any> = {
  INDIVIDUAL: () => <IndividualIcon />,
  BUSINESS: () => <BusinessIcon />,
  EMPLOYEE: () => <EmployeeIcon />,
  PARTNER: () => <TrailSign />,
  TENANT: () => <PnLIcon />,
  SOLUTION: () => <StarIcon />,
  HEAD_TRANSACTION: () => <TransactionIcon />,
  EVENT: () => <FeedbackIcon />,
  ACCOUNT: () => <AccountBalanceWallet />,
};

const useStyles = makeStyles(() => ({
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export default ReferenceField;
